import { useState, type FormEvent } from "react";

const FORM_ID = "platform-feedback-form";
const FORM_ACTION = "https://readdy.ai/api/form/dah8qhjp14a1h3mr6fh0";

type Status = "idle" | "submitting" | "success" | "error";

export default function ContactSection() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setStatus("idle");
    setFormError("");

    const form = event.currentTarget;
    const formData = new FormData(form);

    const decoy = String(formData.get("website_alt") || "").trim();
    if (decoy) {
      setStatus("success");
      form.reset();
      return;
    }

    const message = String(formData.get("message") || "");
    if (message.length > 500) {
      setStatus("error");
      setFormError("留言内容请不要超过 500 个字。");
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      if (typeof value === "string" && value.trim() === "") return;
      payload.append(key, value);
    });

    setStatus("submitting");
    try {
      const response = await fetch(FORM_ACTION, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const code = parsed?.code || "";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (!response.ok || code !== "OK" || isSpam) {
        setStatus("error");
        setFormError(
          isSpam
            ? "提交内容被判定为无效，请检查后重新填写。"
            : serverMsg || "提交失败，请稍后重试。"
        );
        return;
      }

      setStatus("success");
      form.reset();
    } catch {
      setStatus("error");
      setFormError("网络异常，请检查网络连接后重试。");
    }
  };

  return (
    <section id="contact" className="bg-background-50 py-16 md:py-24">
      <div className="mx-auto max-w-shell px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
          <div className="lg:col-span-5">
            <span className="text-xs font-label tracking-[0.2em] text-primary-600 uppercase">Contact</span>
            <h2 className="mt-3 font-heading font-bold text-foreground-950 text-2xl md:text-4xl">推荐平台或给榜单提意见</h2>
            <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-600">
              发现了我们没收录的好阵地？或者觉得某个平台的名次不太对？都可以写在这里。我们会在两周内完成评估，并通过邮件回复你。
            </p>

            <ul className="mt-8 space-y-4">
              <li className="flex items-center gap-3">
                <span className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-100 text-primary-700">
                  <i className="ri-mail-send-line"></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-500">反馈邮箱</p>
                  <a href="mailto:hello@guaquanbang.com" className="text-sm text-foreground-900 hover:text-primary-600 transition-colors cursor-pointer">
                    hello@guaquanbang.com
                  </a>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <span className="w-10 h-10 flex items-center justify-center rounded-md bg-accent-100 text-accent-700">
                  <i className="ri-time-line"></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-500">回复时效</p>
                  <p className="text-sm text-foreground-900">工作日 48 小时内</p>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <span className="w-10 h-10 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                  <i className="ri-shield-star-line"></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-500">上榜原则</p>
                  <p className="text-sm text-foreground-900">免费评估，不接受付费上榜</p>
                </div>
              </li>
            </ul>

            <div className="mt-8 rounded-lg bg-background-100 border border-background-200 p-4">
              <p className="text-xs leading-relaxed text-foreground-600">
                温馨提示：本站仅做平台信息整理与排行参考，不提供任何爆料内容本身，也不对第三方平台的内容真实性负责。
              </p>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="rounded-lg bg-background-100 border border-background-200 p-5 md:p-7">
              <form id={FORM_ID} data-readdy-form={FORM_ID} onSubmit={handleSubmit} noValidate>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="feedback-name" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      你的称呼
                    </label>
                    <input
                      id="feedback-name"
                      name="name"
                      type="text"
                      required
                      placeholder="怎么称呼你"
                      className="w-full px-3.5 py-2.5 text-sm rounded-md bg-background-50 border border-background-300 text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200 transition-colors"
                    />
                  </div>
                  <div>
                    <label htmlFor="feedback-email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      邮箱
                    </label>
                    <input
                      id="feedback-email"
                      name="email"
                      type="email"
                      required
                      placeholder="用于接收回复"
                      className="w-full px-3.5 py-2.5 text-sm rounded-md bg-background-50 border border-background-300 text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200 transition-colors"
                    />
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="feedback-topic" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      反馈类型
                    </label>
                    <select
                      id="feedback-topic"
                      name="topic"
                      defaultValue="推荐平台"
                      className="w-full px-3.5 py-2.5 text-sm rounded-md bg-background-50 border border-background-300 text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200 transition-colors cursor-pointer"
                    >
                      <option value="推荐平台">推荐平台</option>
                      <option value="榜单纠错">榜单纠错</option>
                      <option value="内容建议">内容建议</option>
                      <option value="合作咨询">合作咨询</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="feedback-platform" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      涉及平台（选填）
                    </label>
                    <input
                      id="feedback-platform"
                      name="platform"
                      type="text"
                      placeholder="如：瓜田社"
                      className="w-full px-3.5 py-2.5 text-sm rounded-md bg-background-50 border border-background-300 text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200 transition-colors"
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <label htmlFor="feedback-message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    具体说明
                  </label>
                  <textarea
                    id="feedback-message"
                    name="message"
                    rows={5}
                    maxLength={500}
                    required
                    placeholder="说说推荐理由或你遇到的问题，不超过 500 字"
                    className="w-full px-3.5 py-2.5 text-sm rounded-md bg-background-50 border border-background-300 text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200 transition-colors resize-none"
                  ></textarea>
                  <p className="mt-1.5 text-xs text-foreground-400">最多 500 字，请写清楚便于我们快速评估。</p>
                </div>

                <div className="form-decoy-field" aria-hidden="true">
                  <label htmlFor="website_alt">备用联系电话</label>
                  <input
                    id="website_alt"
                    name="website_alt"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    readOnly
                  />
                </div>

                <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-4">
                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 text-sm rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <i className={status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"}></i>
                    {status === "submitting" ? "提交中..." : "提交反馈"}
                  </button>
                  <p className="text-xs text-foreground-500">
                    提交即表示你同意我们仅将该信息用于榜单评估与回复。
                  </p>
                </div>

                {status === "success" && (
                  <div className="mt-5 flex items-start gap-2.5 rounded-md bg-accent-100 border border-accent-200 p-3.5">
                    <i className="ri-checkbox-circle-fill text-accent-600 mt-0.5"></i>
                    <p className="text-sm text-accent-900">已收到你的反馈，我们会尽快评估并通过邮件回复。</p>
                  </div>
                )}
                {status === "error" && (
                  <div className="mt-5 flex items-start gap-2.5 rounded-md bg-primary-50 border border-primary-200 p-3.5">
                    <i className="ri-error-warning-fill text-primary-600 mt-0.5"></i>
                    <p className="text-sm text-primary-800">{formError}</p>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}